import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStagingJobsGallery, type StagingJobWithContext } from '../../hooks/useStagingJobsGallery';
import { getStructureLabel } from '../../lib/structureTypes';
import type { ToastType } from './Toast';

export type ToastPayload = {
  title?: string;
  message?: string;
  description?: string;
  type?: ToastType;
  thumbnailUrl?: string | null;
  onClick?: () => void;
  action?: { label: string; onClick: () => void };
};

interface StagingJobCompletionToasterProps {
  onToast: (payload: ToastPayload) => void;
}

export function StagingJobCompletionToaster({ onToast }: StagingJobCompletionToasterProps) {
  const navigate = useNavigate();
  const { jobs } = useStagingJobsGallery();
  const initialized = useRef(false);
  const mountedAt = useRef<number>(Date.now());
  const statusById = useRef<Map<string, StagingJobWithContext['status']>>(new Map());

  useEffect(() => {
    if (!initialized.current) {
      jobs.forEach((job) => statusById.current.set(job.id, job.status));
      initialized.current = true;
      return;
    }

    jobs.forEach((job) => {
      const prevStatus = statusById.current.get(job.id);
      statusById.current.set(job.id, job.status);

      const completedAt = job.completed_at ? new Date(job.completed_at).getTime() : null;
      const wasSeen = typeof prevStatus !== 'undefined';
      const completedAfterMount = completedAt ? completedAt > mountedAt.current : false;
      const shouldAnnounce = job.status === 'completed' && (wasSeen || completedAfterMount);

      if (shouldAnnounce && prevStatus !== 'completed') {
        const jobUrl = `/dashboard?tab=results&job=${job.id}`;
        const description = job.structure_type ? getStructureLabel(job.structure_type) : 'Staging job';
        const thumbnail = job.staged_signed_url || job.original_signed_url || null;

        onToast({
          title: 'Staging complete',
          message: job.project_title,
          description,
          type: 'success',
          thumbnailUrl: thumbnail,
          onClick: () => navigate(jobUrl),
        });
      }
    });
  }, [jobs, navigate, onToast]);

  return null;
}
